import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { groupService } from "@/services";
import type { GroupView } from "@/services/types";
import { GROUP_STATUS_LABEL, GROUP_STATUS_BADGE } from "@/config/groups";
import { canDeleteGroup } from "@/config/roles";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Tooltip } from "@/components/base/Tooltip";
import { StatCard } from "@/components/base/StatCard";
import { Tabs } from "@/components/base/Tabs";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";
import { OverviewTab } from "./components/OverviewTab";
import { MembersTab } from "./components/MembersTab";
import { CyclesTab } from "./components/CyclesTab";
import { SettingsTab } from "./components/SettingsTab";
import { ActivateModal } from "./components/ActivateModal";

function LifespanBar({ group }: { group: GroupView }) {
  const total = group.memberCount;
  const completed = group.completedCycles;
  const current = Math.min(completed + 1, total);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {Array.from({ length: total }, (_, i) => {
        const n = i + 1;
        const state = group.status === "draft" ? "upcoming" : n < current ? "done" : n === current ? "current" : "upcoming";
        return (
          <div key={n} className="flex flex-col items-center gap-1" title={`Cycle ${n}`}>
            <div
              className={
                state === "done"
                  ? "h-2.5 w-6 rounded-sm bg-accent-500"
                  : state === "current"
                  ? "h-2.5 w-6 rounded-sm bg-primary-500"
                  : "h-2.5 w-6 rounded-sm bg-background-200"
              }
            />
          </div>
        );
      })}
      <div className="ml-2 flex items-center gap-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-accent-500" />Complete</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-primary-500" />Current</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-background-200" />Upcoming</span>
      </div>
    </div>
  );
}

export default function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isViewer, role } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const canDelete = canDeleteGroup(role);
  const [group, setGroup] = useState<GroupView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("overview");
  const [activateOpen, setActivateOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!group) return;
    const ok = await confirm({
      title: "Delete Group",
      message: `Are you sure you want to permanently delete group "${group.name}" (${group.groupCode})? This action cannot be undone.`,
      confirmLabel: "Delete Group",
      destructive: true,
      dangerText: "All cycles, member enrollments, and transactions for this group will be permanently removed.",
    });
    if (!ok) return;

    setDeleting(true);
    try {
      await groupService.deleteGroup(group.id);
      toast.success("Group deleted", `${group.name} was permanently removed.`);
      navigate("/groups");
    } catch (err) {
      toast.error("Could not delete group", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setGroup(await groupService.getGroupView(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load group.");
      setGroup(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={<BreadcrumbItem label="Groups" to="/groups" />} title="Group" />
        <Card>
          <ErrorState message={error} onRetry={load} />
        </Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div>
        <PageHeader breadcrumb={<BreadcrumbItem label="Groups" to="/groups" />} title="Loading group…" />
        <div className="space-y-3">
          <div className="h-32 animate-pulse rounded-lg bg-background-200" />
          <div className="h-40 animate-pulse rounded-lg bg-background-200" />
        </div>
      </div>
    );
  }

  const remaining = Math.max(0, group.memberCount - group.completedCycles);
  const canManageEnrollment = group.status === "draft" || group.status === "active";
  const filled = group.tickets.filter((t) => Boolean(t.member)).length;
  const organizerTicket = group.tickets.find((t) => t.isOrganizer);
  const organizerAssigned = Boolean(organizerTicket?.member);

  const missing: string[] = [];
  if (filled < group.memberCount) missing.push(`${group.memberCount - filled} more member(s)`);
  if (!organizerAssigned) missing.push("organizer assigned to a ticket");
  const ready = missing.length === 0;

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Groups" to="/groups" />
            <BreadcrumbItem label={group.name} isLast />
          </>
        }
        title={
          <span className="flex items-center gap-2">
            {group.name}
            <Badge variant={GROUP_STATUS_BADGE[group.status]} dot>{GROUP_STATUS_LABEL[group.status]}</Badge>
          </span>
        }
        subtitle={<span className="tnum">{group.groupCode}</span>}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              icon={<i className="ri-bar-chart-box-line" />}
              onClick={() => navigate(`/reports/group-performance?groupId=${group.id}`)}
            >
              Performance report
            </Button>
            {!isViewer && canManageEnrollment ? (
              <>
                {group.status === "draft" ? (
                  ready ? (
                    <Button icon={<i className="ri-rocket-line" />} onClick={() => setActivateOpen(true)}>
                      Activate group
                    </Button>
                  ) : (
                    <Tooltip side="bottom" content={`Activation needs: ${missing.join(" and ")}.`}>
                      <Button disabled icon={<i className="ri-rocket-line" />}>
                        Activate group
                      </Button>
                    </Tooltip>
                  )
                ) : null}
                <Button variant="outline" icon={<i className="ri-user-add-line" />} onClick={() => navigate(`/groups/${group.id}/enrollment`)}>
                  Manage enrolment
                </Button>
              </>
            ) : null}
            {canDelete ? (
              <Button
                variant="outline"
                className="border-danger-200 text-danger-600 hover:bg-danger-50 hover:border-danger-300"
                icon={<i className="ri-delete-bin-line" />}
                loading={deleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            ) : null}
          </div>
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="text-xs text-foreground-500">Monthly installment</p>
            <p className="tnum mt-0.5 text-base font-semibold text-foreground-900"><CurrencyDisplay amount={group.monthlyInstallment} align="left" /></p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Chit value</p>
            <p className="tnum mt-0.5 text-base font-semibold text-foreground-900"><CurrencyDisplay amount={group.chitValue} align="left" /></p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Members</p>
            <p className="tnum mt-0.5 text-base font-semibold text-foreground-900">{group.memberCount}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Duration</p>
            <p className="tnum mt-0.5 text-base font-semibold text-foreground-900">{group.memberCount} months</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Start date</p>
            <p className="tnum mt-0.5 text-base font-semibold text-foreground-900">{formatDate(group.startDate)}</p>
          </div>
        </div>
      </Card>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Cycles completed" value={<span className="tnum">{group.completedCycles}</span>} icon="ri-check-double-line" iconClass="bg-accent-100 text-accent-700" />
        <StatCard label="Cycles remaining" value={<span className="tnum">{remaining}</span>} icon="ri-arrow-right-line" />
        <StatCard label="Next auction" value={<span className="tnum">{group.nextAuctionDate ? formatDate(group.nextAuctionDate) : "—"}</span>} icon="ri-hammer-line" />
        <StatCard label="Total discount so far" value={<CurrencyDisplay amount={group.totalDiscount} hideSymbol />} icon="ri-percent-line" iconClass="bg-secondary-100 text-secondary-700" />
      </div>

      <Card className="mb-4">
        <p className="mb-3 text-xs font-medium text-foreground-500">Lifespan progress</p>
        <LifespanBar group={group} />
      </Card>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { id: "overview", label: "Overview" },
          { id: "members", label: "Members", count: group.tickets.filter((t) => t.member).length },
          { id: "cycles", label: "Cycles", count: group.memberCount },
          { id: "settings", label: "Settings" },
        ]}
      />

      <div className="mt-4">
        {tab === "overview" ? <OverviewTab group={group} isViewer={isViewer} /> : null}
        {tab === "members" ? (
          <MembersTab group={group} isViewer={isViewer} onMemberReplaced={load} />
        ) : null}
        {tab === "cycles" ? <CyclesTab group={group} /> : null}
        {tab === "settings" ? <SettingsTab group={group} isViewer={isViewer} onGroupUpdated={setGroup} /> : null}
      </div>

      <ActivateModal
        open={activateOpen}
        group={group}
        onClose={() => setActivateOpen(false)}
        onActivated={(gid) => {
          load();
          setTab("cycles");
          navigate(`/groups/${gid}`);
        }}
      />
    </div>
  );
}